import React, { useEffect, useState, useCallback } from 'react';
import { displayBookingType } from '../../../constants/bookingTypes';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import './bookingDetails.css';
import '../../SharedView/detailCards.css';
import { statusBadgeClass } from '../../../utils/statusBadge';
import SectionSkeleton from './BookingDetails/SectionSkeleton';
import CleanerDurations from './BookingDetails/CleanerDurations';
import '../Properties/PropertyDetailsComponents/apartmentSetup.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import EditBooking from './EditBooking';
import AddReportModal from "./BookingReports/AddReportModal";
import BookingReports from "./BookingReports/BookingReports";
import ConfirmPopup from '../ConfirmPopup';
import LaundryDetailsData from '../Laundry/LaundryDetails/LaundryDetailsData';
import LaundryDetailsTable from '../Laundry/LaundryDetails/LaundryDetailsTable';
import LaundryImageManager from '../Laundry/LaundryDetails/LaundryImageManager';
import { DateTime } from 'luxon';

import BookingTasks from './BookingTasks/BookingTasks';
import InvoiceDrawer from '../InvoiceDrawer';
import BookingFeesSection from './BookingDetails/BookingFeesSection';
import WalletChargesSection from './BookingDetails/WalletChargesSection';
import BookingPriceChanges from './BookingDetails/BookingPriceChanges';
import BookingDriverStops from './BookingDetails/BookingDriverStops';
import { colorOf } from '../../../constants/areas';

function BookingDetails() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  // Combined counted hours, reported up by the cleaner-durations table.
  const [combinedMinutes, setCombinedMinutes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddReportModalOpen, setIsAddReportModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const [setupPhotos, setSetupPhotos] = useState([]);
  const [assignedAddons, setAssignedAddons] = useState([]);
  const [addonsLoading, setAddonsLoading] = useState(false);
  const [addonsError, setAddonsError] = useState(null);
  const [linenDeposit, setLinenDeposit] = useState(null);

  // Invoice drawer (2026-07-20): generating an invoice opens the shared
  // InvoiceDrawer right here — loading shown INSIDE it, then the full details —
  // instead of the old bare LoadingPopup with a link.
  const [invoiceDrawerOpen, setInvoiceDrawerOpen] = useState(false);
  const [invoiceDrawerId, setInvoiceDrawerId] = useState(null);
  const [invoiceCreating, setInvoiceCreating] = useState(false);
  const [invoiceCreateError, setInvoiceCreateError] = useState(null);
  const [invoiceConfirmOpen, setInvoiceConfirmOpen] = useState(false);

  // Round 7 (PLAN_multi_billing.md): guest invoice — a one-time Zoho customer
  // for a GUEST, never linked to the client's billing.
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const [guestForm, setGuestForm] = useState({ first_name: '', last_name: '', email: '' });
  const [guestSubmitting, setGuestSubmitting] = useState(false);
  const [guestError, setGuestError] = useState('');

  const userRole = (localStorage.getItem('userRole') || '').toLowerCase();

  const handleCopyLink = () => {
    navigator.clipboard.writeText(booking.google_maps_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  /**
   * Sections with their own fetch reload on this tick — 2026-08-03: an edit
   * must show up without reloading the page, and refetching just the affected
   * sections is how. 2026-09-02: the add-ons, linen deposit and card receipts
   * live on this tick too — they were keyed on bookingId alone, so an edit
   * that added an add-on showed nothing until a manual page reload.
   */
  const [refreshTick, setRefreshTick] = useState(0);

  const fetchBookingDetails = useCallback(async () => {
    try {
      const response = await axios.get(`/api/bookings/${bookingId}`);
      setBooking(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching booking details:', error);
      setError('Error fetching booking details.');
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    const fetchAddons = async () => {
      try {
        setAddonsLoading(true);
        const res = await axios.get(`/api/bookings/addons/${bookingId}`);
        setAssignedAddons(res.data?.assignedAddons || []);
        setAddonsError(null);
      } catch (err) {
        console.error('Error fetching add-ons:', err);
        setAddonsError('Failed to load add-ons');
      } finally {
        setAddonsLoading(false);
      }
    };
    if (bookingId) fetchAddons();
  }, [bookingId, refreshTick]);

  useEffect(() => {
    if (!bookingId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`/api/linen-deposits/booking/${bookingId}`);
        if (!cancelled) setLinenDeposit(res.data?.deposit || null);
      } catch (err) {
        console.error('Error fetching linen deposit:', err);
        if (!cancelled) setLinenDeposit(null);
      }
    })();
    return () => { cancelled = true; };
  }, [bookingId, refreshTick]);

  useEffect(() => {
    if (booking?.property_id) {
      const fetchSetupPhotos = async () => {
        try {
          const res = await axios.get(`/api/properties/${booking.property_id}/setup-photos`);
          setSetupPhotos(res.data || []);
        } catch (err) {
          console.error("Error fetching setup photos:", err);
        }
      };
      fetchSetupPhotos();
    }
  }, [booking]);

  useEffect(() => {
    fetchBookingDetails();
  }, [fetchBookingDetails]);

  const handleEdit = () => setIsEditModalOpen(true);

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    fetchBookingDetails();
    setRefreshTick((t) => t + 1);
  };

  const handleAddReport = () => setIsAddReportModalOpen(true);

  const [refreshReports, setRefreshReports] = useState(false);

  const closeAddReportModal = () => {
    setIsAddReportModalOpen(false);
    fetchBookingDetails();
    setRefreshReports(prev => !prev);
  };

  const openModal = (index) => {
    setCurrentIndex(index);
    setIsOpen(true);
  };
  const closeModal = () => setIsOpen(false);

  // ESC to close the photo viewer (matches the property/client album)
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  const images = setupPhotos.map(photo => ({
    original: photo.file_url,
    thumbnail: photo.file_url,
    description: photo.description || undefined
  }));

  if (loading) {
    /* NO full-page spinner swap (owner, 2026-09-02): the page's card shells
       render immediately — white cards, titles, hairlines — and the loading
       happens INSIDE each one, exactly like Booking Fees and Payments. When
       the data lands, content replaces the quiet lines without the layout
       jumping. */
    return (
      <div className="booking-details-wrapper">
        <div className="header header--sticky">
          <div className="header__inner">
            <div className="header-title">
              <h1>Booking ID: #{String(bookingId).padStart(4, '0')}</h1>
              <h2 className="unit-display"><span className="dp-muted">Loading…</span></h2>
            </div>
          </div>
        </div>
        <div className="booking-details-container dp-stack">
          <SectionSkeleton title="Booking Information" rows={4} />
          <SectionSkeleton title="Tasks" rows={3} />
          <SectionSkeleton title="Booking Reports" rows={2} />
          <SectionSkeleton title="Notes" rows={3} />
          <SectionSkeleton title="Apartment Setup Photos" rows={2} />
          <SectionSkeleton title="Booking Fees" rows={3} />
          <SectionSkeleton title="Payments" rows={3} />
        </div>
      </div>
    );
  }

  if (error) return <div>{error}</div>;

  const formattedBookingId = bookingId.toString().padStart(4, '0');

  const getDuration = (start, end) => {
    const startTime = new Date(start);
    const endTime = new Date(end);
    const durationMs = endTime - startTime;
    // Display guard: a bad end (epoch/invalid/before start — e.g. booking #19147
    // stored 1970-01-01) must never render as "-495806 hours". Point-in-time
    // services legitimately have 0 duration; both cases read as "0 hours".
    if (!(durationMs > 0)) return '0 hours';
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours} hour${hours !== 1 ? 's' : ''}${minutes > 0 ? ` ${minutes} minute${minutes !== 1 ? 's' : ''}` : ''}`;
  };

  /**
   * WHEN THE WORK ACTUALLY RAN (2026-08-30).
   *
   * booking_start / booking_end are the PLAN. These are the record:
   * `started_at` is stamped the moment the booking enters In Process — which
   * is the cleaner's before-cleaning video going up — and `completed_at` the
   * moment it is marked Completed. Both ride on the booking row, stamped by
   * trg_bookings_work_times, so no upload path or office edit can forget.
   *
   * Bookings that started before the audit trail the backfill could read have
   * no started_at. Their earliest report upload is the same event seen from
   * the other side, so it stands in — and the line says so on hover rather
   * than passing a guess off as a stamp.
   */
  const startedIso = booking.started_at || booking.first_report_at || null;
  const startedFromReport = !booking.started_at && !!booking.first_report_at;
  const finishedIso = booking.completed_at || null;
  // THE CLEANER TABLE WINS (owner, 2026-09-09: a hand-edited cleaner start
  // must move the actual start and the duration with it). The table reports
  // its earliest start / latest finish — hand-set ones included, naming who
  // set them — and the header shows those; the booking's own stamps are the
  // fallback while the table has nothing.
  const effStartIso = combinedMinutes?.start || startedIso;
  const effFinishIso = combinedMinutes?.end || finishedIso;
  const startEditedBy = combinedMinutes?.startManualBy || null;
  const finishEditedBy = combinedMinutes?.endManualBy || null;
  const isInProcess = String(booking.status_name || '').trim().toLowerCase() === 'in process';
  // Combined counted hours, reported up by the cleaner table below.
  // (Declared here, next to the flags the actual-times row reads.)

  const bookingDay = DateTime.fromISO(booking.booking_start, { zone: 'utc' })
    .setZone('Asia/Dubai')
    .toFormat('yyyy-MM-dd');

  // The clock alone on the booking's own day; the date joins it only when the
  // work ran past midnight, where a bare "01:12" would be a trap.
  const wallTime = (iso) => {
    const d = DateTime.fromISO(iso, { zone: 'utc' }).setZone('Asia/Dubai');
    return d.toFormat('yyyy-MM-dd') === bookingDay
      ? d.toFormat('HH:mm')
      : d.toFormat('d LLL, HH:mm');
  };

  const bedroomDisplay = booking.bedrooms === 0 ? 'S' : booking.bedrooms;
  const unitDisplay = `${booking.client_name} - ${booking.building_name} - Unit ${booking.unit_number} (${bedroomDisplay} - ${booking.bathrooms || 'N/A'})`;


  const areaColor = colorOf(booking.area);

  // Invoice management (generate + the drawer's Send/Apply/Revert/Delete) is
  // MANAGERS ONLY (2026-07-20). Supervisors may still SEE the invoice
  // status, but not edit it.
  // Generating is NOT the same right as running the invoice afterwards.
  // Supervisors may raise one from the booking page (the backend's
  // POST /api/invoices has always been authorizeManagerOrSupervisor), but
  // sending it, applying/reverting payment and deleting stay manager-only —
  // those endpoints are authorizeManager, so showing a supervisor those
  // buttons would only hand them a 403.
  const canGenerateInvoice = userRole === 'manager' || userRole === 'supervisor';
  // Same test the Booking Fees card uses, so the header and the card never
  // disagree about whether this booking has an invoice.
  const hasInvoice = !!(booking?.is_invoiced || booking?.invoice_id || booking?.zoho_invoice_id);
  // Only managers get the drawer. Supervisors and admins click through to the
  // invoice ITSELF in Zoho — they are viewing a document, not running it, and
  // the real document beats our summary of it.
  // 2026-08-09: EVERY staff role opens the drawer; what differs is what
  // they can do inside it — canEdit (send/apply/revert/DELETE) stays manager-
  // only, generate stays manager+supervisor, admin is view-only.
  const canOpenInvoiceDrawer = ['manager', 'supervisor', 'admin'].includes(userRole);
  const canManageInvoice = userRole === 'manager';
  const canViewInvoice = ['manager', 'supervisor', 'admin'].includes(userRole);

  const generateSingleInvoice = async () => {
    if (!bookingId) return;

    // Open the drawer immediately in its "generating" state — the loading lives
    // INSIDE the drawer, then it flips to the full invoice details.
    setInvoiceDrawerOpen(true);
    setInvoiceDrawerId(null);
    setInvoiceCreating(true);
    setInvoiceCreateError(null);

    try {
      const { data } = await axios.post(`/api/invoices/`, { booking_ids: [Number(bookingId)] });
      const localId = data.invoice?.localInvoiceId;
      if (localId) {
        setInvoiceDrawerId(localId); // drawer fetches + shows the details
      } else {
        setInvoiceCreateError('The invoice was created but its id was not returned. Close and reopen it from the Invoices page.');
      }
      fetchBookingDetails(); // refresh is_invoiced / invoice_id on the booking
    } catch (error) {
      console.error("Error generating invoice:", error);
      setInvoiceCreateError(error.response?.data?.message || "Failed to create the invoice. Please try again.");
    } finally {
      setInvoiceCreating(false);
    }
  };

  const handleGenerateInvoice = () => {
    if (booking?.is_invoiced || booking?.invoice_id || booking?.zoho_invoice_id) {
      setInvoiceConfirmOpen(true);
      return;
    }
    generateSingleInvoice();
  };

  const closeInvoiceDrawer = () => {
    setInvoiceDrawerOpen(false);
    setInvoiceDrawerId(null);
    setInvoiceCreating(false);
    setInvoiceCreateError(null);
    fetchBookingDetails();
  };

  // Re-open the invoice drawer for an ALREADY-invoiced booking (manager taps the
  // "Invoiced • INV-…" badge). No create call — straight to the details.
  const openExistingInvoice = () => {
    if (!booking?.invoice_id) return;
    setInvoiceCreating(false);
    setInvoiceCreateError(null);
    setInvoiceDrawerId(booking.invoice_id);
    setInvoiceDrawerOpen(true);
  };

  // Guest invoice (Round 7): small modal → POST /api/invoices/guest → refresh
  // the booking (same path as normal generation) and open the drawer.
  const openGuestInvoiceModal = () => {
    setGuestForm({ first_name: '', last_name: '', email: '' });
    setGuestError('');
    setGuestModalOpen(true);
  };

  const submitGuestInvoice = async () => {
    if (!guestForm.first_name.trim() || !guestForm.last_name.trim() || guestSubmitting) return;
    setGuestSubmitting(true);
    setGuestError('');
    try {
      const { data } = await axios.post('/api/invoices/guest', {
        booking_id: Number(bookingId),
        first_name: guestForm.first_name.trim(),
        last_name: guestForm.last_name.trim(),
        email: guestForm.email.trim() || undefined,
      });
      setGuestModalOpen(false);
      fetchBookingDetails(); // refresh is_invoiced / invoice_id on the booking
      const localId = data.invoice?.id;
      if (localId) {
        setInvoiceCreating(false);
        setInvoiceCreateError(null);
        setInvoiceDrawerId(localId);
        setInvoiceDrawerOpen(true);
      }
    } catch (error) {
      console.error('Error generating guest invoice:', error);
      // 409 already-invoiced etc. — {message} shown verbatim in the modal.
      setGuestError(error.response?.data?.message || 'Failed to create the guest invoice. Please try again.');
    } finally {
      setGuestSubmitting(false);
    }
  };

  return (
    <div className="booking-details-wrapper">
      {/* Full-bleed white bar, content aligned to the page column (2026-08-30).
          The bar itself spans the whole width; header__inner holds the title
          and buttons on exactly the same grid as the cards below, so the
          buttons stop where the cards stop instead of running past them. */}
      <div className="header header--sticky">
       <div className="header__inner">
        <div className="header-title">
          <h1>
            Booking #{formattedBookingId}
            {/* The status lives up here, not as a table fact (owner,
                2026-09-02) — one glance at the title says both. */}
            <span className={`${statusBadgeClass(booking.status_name)} bd-headStatus`}>{booking.status_name}</span>
            {String(booking.status_name).trim().toLowerCase() === 'pending' && booking.pending_reason && (
              <span className="bd-headReason">Reason: {booking.pending_reason}</span>
            )}
            {/* The client rides the id row (owner, 2026-09-03) — long names
                made the one subline wrap; splitting property and client over
                the two rows gives each a full line. */}
            <span className="bd-sep">·</span>
            <a href={`/clients/${booking.client_id}`} className="bd-who">{booking.client_name}</a>
            <span className="bd-idChip">#{String(booking.client_id).padStart(5, '0')}</span>
            <a href={`/clients/${booking.client_id}`} className="bd-headerLink">Open client ↗</a>
          </h1>
          {/* One subline, one type size: the client with their link beside
              them, the unit with its link beside it, and the property id as a
              chip of the same weight — no more tiny letters next to big
              buttons (owner, 2026-09-02). */}
          <div className="bd-subline">
            <a
              href={`/properties/${booking.property_id}`}
              style={{ color: areaColor }}
              className="bd-unit"
            >
              {unitDisplay.replace(`${booking.client_name} - `, '')}
            </a>
            <span className="bd-idChip">#{booking.property_id.toString().padStart(5, '0')}</span>
            <a href={`/properties/${booking.property_id}`} className="bd-headerLink">Open property ↗</a>
          </div>
        </div>

        <div className='booking-buttons'>
          {/* OPEN only, never GENERATE (owner, 2026-09-03): a generate button
              in the header invited accidental invoices — creation lives in
              the summary flows. Once an invoice exists, opening it stays one
              click away. */}
          {!booking.is_deleted && canGenerateInvoice && hasInvoice && (
            <button type="button" className="invoice-header-button" onClick={openExistingInvoice}>
              Open Invoice
            </button>
          )}
          {!booking.is_deleted && <button className="add-report-button" onClick={handleAddReport}>Add Report</button>}
          {!booking.is_deleted && <button className="edit-button" onClick={handleEdit}>Edit Booking</button>}
        </div>
       </div>
      </div>

      {booking.is_deleted && (
        <div className="booking-deleted-banner">
          <div className="booking-deleted-banner__icon">⚠️</div>
          <div className="booking-deleted-banner__content">
            <div className="booking-deleted-banner__title">This booking has been deleted</div>
            <div className="booking-deleted-banner__meta">
              {booking.deleted_by_name && <>Deleted by <strong>{booking.deleted_by_name}</strong></>}
              {booking.deleted_at && <> on {DateTime.fromISO(booking.deleted_at, { zone: 'utc' }).setZone('Asia/Dubai').toFormat("d LLL yyyy, HH:mm")}</>}
            </div>
          </div>
        </div>
      )}

      <div className="booking-details-container dp-stack">
        {/* ONE card (owner, 2026-09-02): the side-by-side pair wasted half
            its area. Booking facts flow full-width; the property facts follow
            as a sub-section, the way the client card carries its billing
            address. */}
        <section className="dp-card booking-section">
          <h3>Booking Information</h3>
          <div className="dp-grid">
              {/* Row proportions (owner, 2026-09-03): the three facts with
                  real text get the width; the two Yes/No chips take little. */}
              <div className="dp-item dp-item--roomy">
                <span className="dp-label">Start</span>
                <span className="dp-value">{DateTime.fromISO(booking.booking_start, { zone: 'utc' }).setZone('Asia/Dubai').toFormat("d LLL yyyy, HH:mm")}</span>
              </div>
              <div className="dp-item dp-item--roomy">
                <span className="dp-label">Type</span>
                <span className="dp-value">{displayBookingType(booking.booking_type)}</span>
              </div>
              <div className="dp-item dp-item--roomy">
                <span className="dp-label">Duration</span>
                <span className="dp-value">
                  {Number(booking.required_cleaners) > 0
                    ? `${booking.required_cleaners} cleaner${Number(booking.required_cleaners) > 1 ? 's' : ''} · `
                    : ''}
                  {getDuration(booking.booking_start, booking.booking_end)}
                </span>
              </div>
              <div className="dp-item dp-item--tight">
                <span className="dp-label">Amenities</span>
                <span className="dp-value">{booking.is_amenities ? <span style={{ color: "green" }}>Yes</span> : <span style={{ color: "red" }}>No</span>}</span>
              </div>
              <div className="dp-item dp-item--tight">
                <span className="dp-label">Laundry</span>
                <span className="dp-value">
                  {booking.is_laundry ? (
                    <span style={{ color: "green" }}>
                      Yes
                      {booking.has_laundry_counts && (
                        <a
                          href={`/laundry/${bookingId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ marginLeft: "10px", color: "#4c7ac5" }}
                        >
                          <FontAwesomeIcon icon={faExternalLinkAlt} />
                        </a>
                      )}
                    </span>
                  ) : (
                    <span style={{ color: "red" }}>No</span>
                  )}
                </span>
              </div>
              {(effStartIso || effFinishIso) && (
                <>
                  <div className="dp-item dp-item--roomy">
                    <span className="dp-label">Actual start</span>
                    <span className="dp-value">
                      {effStartIso ? (
                        <span title={startEditedBy
                          ? 'The earliest cleaner start in the table below, set by hand'
                          : startedFromReport
                            ? 'Read from the first report uploaded on this booking — this one predates the completion trail'
                            : 'When the booking went into In Process'}>
                          {wallTime(effStartIso)}
                          {/* What the time is BASED ON (owner, 2026-09-03):
                              first report, the cleaner starting in the app, a
                              staff member changing the status — or, since
                              2026-09-09, a cleaner start set by hand. */}
                          {startEditedBy ? (
                            <span className="bd-actual-source"> (cleaner start edited manually by {startEditedBy})</span>
                          ) : startedFromReport ? (
                            <span className="bd-actual-source"> (first report)</span>
                          ) : booking.started_by ? (
                            <span className="bd-actual-source">
                              {booking.started_by.role === 'cleaner'
                                ? ` (started in app by ${booking.started_by.name})`
                                : ` (status set by ${booking.started_by.name})`}
                            </span>
                          ) : null}
                        </span>
                      ) : 'not recorded'}
                    </span>
                  </div>
                  <div className="dp-item dp-item--roomy">
                    <span className="dp-label">Actual finish</span>
                    <span className="dp-value">
                      {effFinishIso ? (
                        <span title={finishEditedBy ? 'The latest cleaner finish in the table below, set by hand' : 'When the booking was marked Completed'}>
                          {wallTime(effFinishIso)}
                          {/* What the finish is BASED ON (owner, 2026-09-06):
                              approving the after-cleaning report auto-completes
                              the booking, so the approver's completion IS the
                              report; anyone else changed the status by hand —
                              or, since 2026-09-09, a cleaner finish set by hand. */}
                          {finishEditedBy ? (
                            <span className="bd-actual-source"> (cleaner finish edited manually by {finishEditedBy})</span>
                          ) : booking.completed_by ? (
                            <span className="bd-actual-source">
                              {booking.completed_by.via_report
                                ? ` (after cleaning report approved by ${booking.completed_by.name})`
                                : ` (status set by ${booking.completed_by.name})`}
                            </span>
                          ) : null}
                        </span>
                      ) : (isInProcess ? 'still in progress' : 'not recorded')}
                    </span>
                  </div>
                  <div className="dp-item dp-item--roomy dp-item--grow">
                    <span className="dp-label">Actual duration</span>
                    <span className="dp-value">
                      {/* Multiple cleaners → the COMBINED figure is the charge,
                          so it carries the weight; the wall-clock elapsed steps
                          back. One cleaner → the elapsed IS the figure. */}
                      {combinedMinutes?.minutes != null && combinedMinutes.cleaners > 1 ? (
                        <>
                          {effStartIso && effFinishIso && (
                            <span className="bd-actual-source" title="Between the actual start and the actual finish">{getDuration(effStartIso, effFinishIso)} · </span>
                          )}
                          <span title="The sum of every cleaner's counted hours — per-cleaner clocks in the table below">
                            {`${Math.floor(combinedMinutes.minutes / 60)}h ${String(combinedMinutes.minutes % 60).padStart(2, '0')}m`} all cleaners combined
                          </span>
                          <span className="bd-actual-source"> · see below</span>
                        </>
                      ) : (
                        effStartIso && effFinishIso
                          ? <span title="Between the actual start and the actual finish">{getDuration(effStartIso, effFinishIso)}</span>
                          : (isInProcess ? 'still in progress' : '—')
                      )}
                    </span>
                  </div>
                </>
              )}
              <div className="dp-item dp-item--full">
                <span className="dp-label">Add-ons</span>
                <span className="dp-value">
                  {addonsLoading ? (
                    <span className="dp-muted">Loading…</span>
                  ) : addonsError ? (
                    <span style={{ color: "red" }}>Failed to load</span>
                  ) : assignedAddons.length === 0 ? (
                    <span className="dp-muted">None</span>
                  ) : (
                    <span className="addons-badges">
                      {assignedAddons.map((a) => (
                        <span key={a.id ?? a.item_id} className="addon-badge" title={`${a.name}× ${a.quantity}${a.is_custom ? ` · custom, AED ${a.default_price}` : ''}`}>
                          {a.name}<span className="addon-qty">× {String(Number(a.quantity)).replace(/\.0+$/, '')}</span>
                        </span>
                      ))}
                    </span>
                  )}
                </span>
              </div>
          </div>
          <div className="dp-subSection">
            <div className="dp-grid">
              {/* The unit IS the fact, area-coded like the header; the fact
                  links are enough on their own — the header carries the open
                  buttons (owner, 2026-09-02). Row two repeats row one's
                  widths so the columns line up. */}
              <div className="dp-item dp-item--wide">
                <span className="dp-label">Property</span>
                <span className="dp-value">
                  <a href={`/properties/${booking.property_id}`} style={{ color: areaColor }}>{unitDisplay.replace(`${booking.client_name} - `, '')}</a>
                </span>
              </div>
              <div className="dp-item dp-item--col2">
                <span className="dp-label">Client</span>
                <span className="dp-value">
                  <a href={`/clients/${booking.client_id}`}>{booking.client_name}</a>
                </span>
              </div>
              <div className="dp-item dp-item--col3">
                <span className="dp-label">Beds</span>
                <span className="dp-value">{booking.double_beds} double · {booking.single_beds} single{Number(booking.super_king_beds) > 0 ? ` · ${booking.super_king_beds} super king` : ''}</span>
              </div>
              <div className="dp-item dp-item--slim">
                <span className="dp-label">Area</span>
                <span className="dp-value">{booking.area || '—'}</span>
              </div>
              <div className="dp-item dp-item--wide">
                <span className="dp-label">Property access</span>
                <span className="dp-value">{booking.property_access || '—'}</span>
              </div>
              <div className="dp-item dp-item--col2">
                <span className="dp-label">Wi-Fi name</span>
                <span className="dp-value">{booking.wifi_name || '—'}</span>
              </div>
              <div className="dp-item dp-item--col3">
                <span className="dp-label">Wi-Fi password</span>
                <span className="dp-value">{booking.wifi_password || '—'}</span>
              </div>
              {booking.google_maps_link && (
                <div className="dp-item dp-item--slim">
                  <span className="dp-label">Location</span>
                  <span className="dp-value">
                    <span className="google-maps-link-row">
                      <a className="dp-mapsBtn" href={booking.google_maps_link} target="_blank" rel="noopener noreferrer">
                        Open in Google Maps ↗
                      </a>
                      <span className="icon-button" onClick={handleCopyLink}>
                        <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                      </span>
                    </span>
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Cleaners close the card (owner, 2026-09-02): facts, property,
              then who cleaned and for how long — three quiet bands. */}
          <CleanerDurations
            bookingId={bookingId}
            bookingStart={booking.booking_start}
            startedIso={startedIso}
            finishedIso={finishedIso}
            isInProcess={isInProcess}
            cleanerNames={booking.cleaner_names}
            onCombined={setCombinedMinutes}
          />
          {!(startedIso && (finishedIso || isInProcess) && booking.cleaner_names && !/^no cleaners/i.test(booking.cleaner_names)) && (
            <div className="dp-subSection">
              <div className="dp-subTitle">Cleaners</div>
              <span className="dp-value">
                {booking.cleaner_names}
                <BookingDriverStops bookingId={bookingId} bookingStart={booking.booking_start} />
              </span>
            </div>
          )}
        </section>

        <BookingTasks bookingId={bookingId} clientId={booking.client_id} propertyId={booking.property_id} />

        <section id="booking-reports" className="dp-card booking-section reports-section">
          <div className='booking-reports-header dp-cardHead'>
            <h3>Booking Reports</h3>
            <button className="add-report-button" onClick={handleAddReport}>Add Report</button>
          </div>
          <BookingReports bookingId={bookingId} refreshSignal={refreshReports} />
        </section>

        <section className="dp-card booking-section notes-section">
          <h3>Notes</h3>
          <div className="dp-tiles">
            {[
              ['Permanent', [booking.permanent_notes, booking.all_properties_notes].filter(Boolean).join('\n\n')],
              ['Next cleaning', booking.dissapearing_note],
              ['Booking specific', booking.booking_specific_notes],
              ['Warehouse (permanent)', booking.warehouse_note],
              ['Warehouse (this booking)', booking.booking_warehouse_notes],
            ].map(([title, text]) => (
              <div key={title} className="dp-tile">
                <div className="dp-tileTitle">{title}</div>
                <div className={`dp-tileText ${text ? '' : 'dp-tileText--empty'}`}>{text || 'No note'}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="dp-card booking-section setup-photos-section">
          <h3>Apartment Setup Photos</h3>
          {images.length > 0 ? (
            <div className="aps-grid">
              {images.map((image, index) => (
                <div key={index} className="aps-tile">
                  <div className="aps-tileImgWrap" onClick={() => openModal(index)}>
                    <img src={image.thumbnail} alt={`Setup ${index}`} className="aps-tileImg" />
                  </div>
                  {image.description && (
                    <div className="aps-cap" title={image.description}>{image.description}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-photos-message"><p>No setup photos available.</p></div>
          )}
        </section>

        {isOpen && (
          <div className="apsModal" role="dialog" aria-modal="true" onClick={closeModal}>
            <button
              type="button"
              className="modal-x"
              onClick={(e) => { e.stopPropagation(); closeModal(); }}
              aria-label="Close"
              title="Close"
            >
              <span aria-hidden="true">×</span>
            </button>
            <div className="apsModal__content" onClick={(e) => e.stopPropagation()}>
              <ImageGallery
                items={images}
                startIndex={currentIndex}
                showThumbnails={false}
                showFullscreenButton={false}
                showPlayButton={false}
                showBullets={false}
                showIndex={true}
                slideDuration={350}
                lazyLoad={true}
                showNav={true}
                renderLeftNav={(onClick, disabled) => (
                  <button type="button" className="apsNav apsNav--left" onClick={onClick} disabled={disabled} aria-label="Previous photo">
                    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                      <path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                )}
                renderRightNav={(onClick, disabled) => (
                  <button type="button" className="apsNav apsNav--right" onClick={onClick} disabled={disabled} aria-label="Next photo">
                    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                      <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                )}
              />
            </div>
          </div>
        )}

        {booking.has_laundry_counts && (
          <section className="dp-card booking-section laundry-details-section">
            <h3><a href={`/laundry/${bookingId}`} className="info-link">Laundry Details</a></h3>
            <LaundryDetailsData bookingId={bookingId} />
            <LaundryDetailsTable bookingId={bookingId} />
            <LaundryImageManager bookingId={bookingId} />
          </section>
        )}

        {linenDeposit && (
          <div className="linen-deposit-banner">
            <div className="linen-deposit-banner__icon">ℹ️</div>
            <div className="linen-deposit-banner__content">
              <div className="linen-deposit-banner__title">
                One-time linen deposit (AED {Number(linenDeposit.amount) % 1 === 0 ? Number(linenDeposit.amount) : Number(linenDeposit.amount).toFixed(2)}) was charged on this booking — first linen-hire booking for this property.
              </div>
              <div className="linen-deposit-banner__meta">
                Fully refundable when the client stops using the service (linens returned in good condition).
              </div>
            </div>
          </div>
        )}

        <BookingFeesSection
          booking={booking}
          canManageInvoice={canGenerateInvoice}
          canOpenDrawer={canOpenInvoiceDrawer}
          canViewInvoice={canViewInvoice}
          onGenerateInvoice={handleGenerateInvoice}
          onOpenInvoice={openExistingInvoice}
          onGenerateGuestInvoice={openGuestInvoiceModal}
        />

        <WalletChargesSection bookingId={bookingId} refreshTick={refreshTick} />

        {/* Price increases after payment — client must approve/dispute in 3 days */}
        <BookingPriceChanges bookingId={bookingId} refreshTick={refreshTick} />

        {isEditModalOpen && (
          <div className="popup-overlay">
            <div className="popup-content">
              <button type="button" className="modal-x" onClick={closeEditModal}>&times;</button>
              <EditBooking bookingId={bookingId} onClose={closeEditModal} />
            </div>
          </div>
        )}

        {isAddReportModalOpen && (
          <div className="popup-overlay">
            <div className="popup-content">
              <button type="button" className="modal-x" onClick={closeAddReportModal}>&times;</button>
              <AddReportModal bookingId={bookingId} onClose={closeAddReportModal} />
            </div>
          </div>
        )}

        {guestModalOpen && (
          <div className="guest-inv-overlay" onClick={() => !guestSubmitting && setGuestModalOpen(false)}>
            <div className="guest-inv-modal" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="guest-inv-modal__close"
                aria-label="Close"
                onClick={() => !guestSubmitting && setGuestModalOpen(false)}
              >
                ×
              </button>
              <h3 className="guest-inv-modal__title">Generate Guest Invoice</h3>
              <p className="guest-inv-modal__note">
                Creates a one-time Zoho customer for the guest — not linked to the
                client&apos;s billing.
              </p>
              <div className="guest-inv-modal__grid">
                <label className="guest-inv-modal__label">
                  Guest first name *
                  <input
                    type="text"
                    maxLength={80}
                    value={guestForm.first_name}
                    onChange={(e) => setGuestForm((p) => ({ ...p, first_name: e.target.value }))}
                  />
                </label>
                <label className="guest-inv-modal__label">
                  Guest last name *
                  <input
                    type="text"
                    maxLength={80}
                    value={guestForm.last_name}
                    onChange={(e) => setGuestForm((p) => ({ ...p, last_name: e.target.value }))}
                  />
                </label>
                <label className="guest-inv-modal__label guest-inv-modal__label--wide">
                  Email (optional)
                  <input
                    type="email"
                    maxLength={120}
                    value={guestForm.email}
                    onChange={(e) => setGuestForm((p) => ({ ...p, email: e.target.value }))}
                  />
                </label>
              </div>
              {!!guestError && <div className="guest-inv-modal__error">{guestError}</div>}
              <div className="guest-inv-modal__actions">
                <button
                  type="button"
                  className="guest-inv-modal__btn guest-inv-modal__btn--ghost"
                  onClick={() => setGuestModalOpen(false)}
                  disabled={guestSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="guest-inv-modal__btn guest-inv-modal__btn--primary"
                  onClick={submitGuestInvoice}
                  disabled={guestSubmitting || !guestForm.first_name.trim() || !guestForm.last_name.trim()}
                  title={!guestForm.first_name.trim() || !guestForm.last_name.trim()
                    ? 'Guest first and last name are required' : undefined}
                >
                  {guestSubmitting ? 'Creating…' : 'Create Guest Invoice'}
                </button>
              </div>
            </div>
          </div>
        )}

        <InvoiceDrawer
          open={invoiceDrawerOpen}
          invoiceId={invoiceDrawerId}
          creating={invoiceCreating}
          createError={invoiceCreateError}
          canEdit={canManageInvoice}
          onRetryCreate={generateSingleInvoice}
          onClose={closeInvoiceDrawer}
          onChanged={fetchBookingDetails}
        />

        <ConfirmPopup
          isOpen={invoiceConfirmOpen}
          title="Update Existing Invoice"
          message="This booking already belongs to an invoice. The existing invoice will be updated with fresh prices (no duplicate created). If other bookings are on the same invoice, they'll be refreshed too to keep the invoice consistent. Continue?"
          confirmLabel="Update Invoice"
          cancelLabel="Cancel"
          isDestructive={false}
          onConfirm={() => {
            setInvoiceConfirmOpen(false);
            generateSingleInvoice();
          }}
          onCancel={() => setInvoiceConfirmOpen(false)}
        />
      </div>
    </div>
  );
}

export default BookingDetails;